import { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * Interfaz base para items buscables en selects
 */
export interface SearchableSelectItem {
  id: number;
  nc?: string;
  denominacion: string;
  [key: string]: unknown;
}

interface UseSearchableSelectOptions {
  /**
   * Selector CSS del contenedor para detectar clicks fuera
   * @default '.searchable-select-container'
   */
  containerSelector?: string;
  /**
   * Campos adicionales para buscar además de nc y denominacion
   */
  searchFields?: string[];
}

/**
 * Hook reutilizable para manejar selects con búsqueda
 * Maneja estado de búsqueda, selección y dropdown
 * 
 * @param items - Array de items a buscar
 * @param options - Opciones de configuración
 * 
 * @example
 * const { searchText, selectedItem, filteredItems, handleSelect } = useSearchableSelect(clientes);
 */
export const useSearchableSelect = <T extends SearchableSelectItem>(
  items: T[] = [],
  options: UseSearchableSelectOptions = {}
) => {
  const { containerSelector = '.searchable-select-container', searchFields = [] } = options;
  
  const [searchText, setSearchText] = useState('');
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  /**
   * Filtra items basado en el texto de búsqueda
   */
  const filteredItems = useMemo(() => {
    if (!searchText.trim()) {
      return items;
    }

    const search = searchText.toLowerCase();
    return items.filter(item => {
      // Buscar en nc
      if (item.nc?.toLowerCase().includes(search)) {
        return true;
      }
      
      // Buscar en denominacion
      if (item.denominacion.toLowerCase().includes(search)) {
        return true;
      }
      
      // Buscar en campos adicionales
      return searchFields.some(field => {
        const fieldValue = item[field];
        return fieldValue && String(fieldValue).toLowerCase().includes(search);
      });
    });
  }, [items, searchText, searchFields]);

  /**
   * Maneja la selección de un item
   */
  const handleSelect = useCallback((item: T) => {
    setSelectedItem(item);
    const displayText = item.nc ? `${item.nc} - ${item.denominacion}` : item.denominacion;
    setSearchText(displayText);
    setShowDropdown(false);
  }, []);

  /**
   * Limpia la selección (acción del usuario — abre el dropdown)
   */
  const handleClear = useCallback(() => {
    setSelectedItem(null);
    setSearchText('');
    setShowDropdown(true);
  }, []);

  /**
   * Limpia la selección desde el padre — NO abre el dropdown
   */
  const clearSelection = useCallback(() => {
    setSelectedItem(null);
    setSearchText('');
    setShowDropdown(false);
  }, []);

  /**
   * Maneja el cambio en el texto de búsqueda
   */
  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value);
    setSelectedItem(null);
    setShowDropdown(true);
  }, []);

  /**
   * Cierra el dropdown cuando se hace clic fuera del contenedor
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(containerSelector)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [containerSelector]);

  return {
    searchText,
    selectedItem,
    showDropdown,
    filteredItems,
    handleSelect,
    handleClear,
    handleSearchChange,
    clearSelection,
    setShowDropdown,
  };
};

